import {Avatar} from "@ui";
import PropTypes from "prop-types";
import {useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import useGamanzaWidgetHydration from "../../../hooks/useGamanzaWidgetHydration";
import useNotificationsFeed from "../../../hooks/notifications/useNotificationsFeed";
import useIsMobile from "../../../hooks/useIsMobile";
import {useAuthStore} from "../../../store/useAuth.store";
import {getUserAvatarImage, getUserInitials} from "../../../util/avatarUtils";
import {useIsGamanzaUiEnabled} from "../../../utils/gamanzaUi";
import UserMenu from "../UserMenu";
import styles from "./avatar.module.css";

const MOBILE_BREAKPOINT = 799;
const GAMANZA_AVATAR_DATA_SIZE = 50;

/**
 * Header avatar: Gamanza widget when UI gate is on; otherwise local pack /
 * AvatarEditor image (same as profile) so production without gamification
 * does not show a permanent skeleton.
 */
const UserAvatar = ({size = 40}) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile({breakpoint: MOBILE_BREAKPOINT});
  const hostRef = useRef(null);
  const [imageError, setImageError] = useState(false);

  const {unreadCount} = useNotificationsFeed();
  const logout = useAuthStore(state => state.logout);
  const authStatus = useAuthStore(state => state.authStatus);
  const token = useAuthStore(state => state.token);
  const user = useAuthStore(state => state.user);
  const gamanzaUiEnabled = useIsGamanzaUiEnabled();

  const numericSize = typeof size === "number" ? size : Number.parseInt(String(size), 10) || 40;
  const isAuthenticated = authStatus === "authenticated" && Boolean(token);
  const hydrateWidgets = gamanzaUiEnabled && isAuthenticated;
  const {isHydrating} = useGamanzaWidgetHydration({
    enabled: hydrateWidgets,
    token,
    rootRef: hostRef,
  });
  const showSkeleton = hydrateWidgets && isHydrating;
  const showNotificationBadge = isMobile && unreadCount > 0;

  const avatarImage = getUserAvatarImage(user);
  const userInitials = getUserInitials(user);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <UserMenu handleLogout={handleLogout}>
      <div
        className={styles.avatarWrapper}
        style={{"--header-avatar-size": `${numericSize}px`}}
      >
        {gamanzaUiEnabled ? (
          <div
            ref={hostRef}
            className={`${styles.gamanzaAvatarHost} ${showNotificationBadge ? styles.avatarPulse : ""}`}
            aria-busy={showSkeleton}
            aria-hidden
          >
            {showSkeleton && <span className={styles.avatarSkeleton} />}
            <div
              className={`gamification_widget ${styles.gamanzaAvatarWidget} ${
                showSkeleton ? styles.widgetPending : styles.widgetReady
              }`}
              data-type="avatar"
              data-size={String(GAMANZA_AVATAR_DATA_SIZE)}
            />
          </div>
        ) : (
          <Avatar
            size={numericSize}
            className={`${styles.avatar_icon} ${showNotificationBadge ? styles.avatarPulse : ""}`}
            style={{background: "var(--surface-2)"}}
          >
            {!imageError && avatarImage ? (
              <img
                src={avatarImage}
                alt=""
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "50%",
                }}
                onError={() => setImageError(true)}
              />
            ) : (
              userInitials
            )}
          </Avatar>
        )}
        {showNotificationBadge && (
          <span className={styles.notificationBadge}>{unreadCount > 99 ? "99+" : unreadCount}</span>
        )}
      </div>
    </UserMenu>
  );
};

UserAvatar.propTypes = {
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default UserAvatar;
